import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, Truck, ArrowLeft } from "lucide-react";
import { NavBar } from "@/components/NavBar";
import { Skeleton } from "@/components/ui/skeleton";
import { OptionPicker, ColourField } from "@/components/checkout/OptionPicker";
import { api, formatINR, BEAUTY_CAT, normalizeImageRef } from "@/lib/lamka";
import { SafeImg } from "@/components/SafeImg";
import { splitOptions } from "@/lib/order";
import { useAuth } from "@/context/AuthContext";
import { useLikeToggle } from "@/hooks/useLikeToggle";
import { MessageSellerButton } from "@/components/chat/MessageSellerButton";
import { usePageTitle } from "@/hooks/usePageTitle";
import { FollowButton } from "@/context/FollowsContext";
import { AdminListingBar } from "@/components/admin/AdminListingBar";
import { SwipeGallery } from "@/components/SwipeGallery";
import { useProductSwipe, ProductPager } from "@/components/ProductSwipe";
import { ShareButtons } from "@/components/ShareButtons";

export default function Product() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState("");
  const [colour, setColour] = useState("");
  const toggle = useLikeToggle((fn) => setItem((it) => fn([it])[0]));
  const buyRef = useRef(null);
  const [buyVisible, setBuyVisible] = useState(true);
  usePageTitle(item?.title);

  useEffect(() => {
    let stale = false;
    setItem((it) => (it?.id === id ? it : null));
    api.get(`/listings/${id}`).then((r) => !stale && setItem(r.data)).catch(() => !stale && setError(true));
    return () => { stale = true; };
  }, [id, user?.user_id]);

  useEffect(() => {
    setActive(0);
    setSize("");
    setColour("");
    setError(false);
  }, [id]);

  const swipe = useProductSwipe(id);

  // Show the sticky mobile buy bar whenever the main Buy button is off-screen
  useEffect(() => {
    const el = buyRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setBuyVisible(e.isIntersecting), { rootMargin: "0px 0px -40px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [item?.id]);

  if (error) return <Shell><p data-testid="product-error" className="font-display text-2xl">This piece is no longer available.</p></Shell>;
  if (!item) return <Shell><Skeleton className="aspect-[3/4] max-w-xl rounded-none bg-[#F5EFE6]" /></Shell>;

  const sizes = splitOptions(item.size);
  const colours = splitOptions(item.colour);
  const pickedSize = size || (sizes.length === 1 ? sizes[0] : "");
  const pickedColour = colour.trim();
  const soldOut = item.stock === 0;
  const isOwn = user && user.user_id === item.seller_id;
  const missing = (sizes.length > 1 && !pickedSize) || (colours.length > 1 && !pickedColour);
  const refs = (item.images || []).map(normalizeImageRef).filter(Boolean);
  const images = refs.length ? refs : [null];

  const buyNow = () => {
    if (!user) return navigate("/login");
    const q = new URLSearchParams({ qty: "1", ...(pickedSize && { size: pickedSize }), ...(pickedColour && { colour: pickedColour }) });
    navigate(`/checkout/${item.id}?${q}`);
  };

  return (
    <Shell>
      <ProductPager {...swipe} />
      <div {...swipe.handlers} className={`grid lg:grid-cols-[1.1fr_1fr] gap-8 sm:gap-10 lg:gap-20 transition-[transform,opacity] duration-200 ${swipe.slideClass}`}>
        <SwipeGallery key={item.id} images={images} title={item.title} square={item.category === BEAUTY_CAT} />
        <div className="hidden sm:flex flex-row gap-4">
          {images.length > 1 && (
            <div className="flex flex-col gap-3 overflow-y-auto">
              {images.map((src, i) => (
                <button key={i} data-testid={`product-thumb-${i}`} onClick={() => setActive(i)} className={`shrink-0 w-16 ${item.category === BEAUTY_CAT ? "aspect-square" : "aspect-[3/4]"} overflow-hidden border ${active === i ? "border-[#C5A059]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                  <SafeImg src={src} compact className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className={`relative flex-1 ${item.category === BEAUTY_CAT ? "aspect-square" : "aspect-[3/4]"} bg-[#F5EFE6] overflow-hidden`}>
            <SafeImg data-testid="product-main-image" src={images[active]} alt={item.title} className="h-full w-full object-cover fade-up" key={active} />
          </div>
        </div>

        <div className="lg:pt-6 space-y-8 min-w-0">
          {isAdmin && <AdminListingBar item={item} onUpdated={(u) => setItem((it) => ({ ...it, ...u }))} />}
          <div>
            <p className="eyebrow mb-4">{item.category === BEAUTY_CAT ? "Beauty" : item.category}</p>
            <h1 data-testid="product-detail-title" className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#0F0F0F] break-words">{item.title}</h1>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#71717A] mt-4">by <Link to={`/seller/${item.seller_id}`} data-testid="product-seller-link" className="underline underline-offset-4 hover:text-[#B38F46]">{item.seller_name || "Lamka seller"}</Link></p>
            <FollowButton sellerId={item.seller_id} name={item.seller_name} tone="light" className="mt-4" />
          </div>
          <p data-testid="product-detail-price" className="font-mono text-2xl text-[#0F0F0F] break-all">{formatINR(item.price)}</p>
          {item.description && <p className="text-sm sm:text-base text-[#3f3f46] leading-relaxed whitespace-pre-line">{item.description}</p>}
          <OptionPicker label="Size" options={sizes} value={pickedSize} onChange={setSize} testid="size" />
          <ColourField raw={item.colour} options={colours} value={colour} onChange={setColour} />

          <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs font-mono uppercase tracking-[0.18em] text-[#71717A] border-y border-[#C5A059]/25 py-4">
            {item.delivery_time && <span data-testid="product-delivery" className="flex items-center gap-2"><Truck className="h-4 w-4" strokeWidth={1.5} /> {item.delivery_time}</span>}
            <span data-testid="product-stock">{soldOut ? "Sold out" : `${item.stock} in stock`}</span>
            {item.location && <span>{item.location}</span>}
          </div>

          <div ref={buyRef} className="flex gap-3">
            <button
              data-testid="buy-now-button"
              disabled={soldOut || isOwn || missing}
              onClick={buyNow}
              className="flex-1 bg-[#0F0F0F] text-[#C5A059] py-4 text-xs tracking-[0.3em] uppercase transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_30px_rgba(197,160,89,0.35)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {soldOut ? "Sold out" : isOwn ? "Your listing" : missing ? "Select options" : "Buy now"}
            </button>
            <button data-testid="product-like-button" aria-label="Save" onClick={() => toggle(item)} className="w-14 border border-[#0F0F0F]/20 flex items-center justify-center hover:border-[#0F0F0F] transition-colors">
              <Heart className={`h-5 w-5 ${item.is_liked ? "fill-[#C5A059] text-[#C5A059]" : ""}`} strokeWidth={1.5} />
            </button>
          </div>
          {!isOwn && <MessageSellerButton listingId={item.id} className="w-full border border-[#0F0F0F] py-4 hover:bg-[#0F0F0F] hover:text-[#C5A059]" />}
          <ShareButtons item={item} />
        </div>
      </div>
      {!buyVisible && !isOwn && (
        <div data-testid="mobile-buy-bar" className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-[#FBF9F5]/95 backdrop-blur-md border-t border-[#C5A059]/30 px-4 pt-3 pb-safe flex items-center gap-3 fade-up">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-[#71717A] truncate">{item.title}</p>
            <p className="font-mono text-base text-[#0F0F0F] truncate">{formatINR(item.price)}</p>
          </div>
          <button data-testid="mobile-like-button" aria-label="Save" onClick={() => toggle(item)} className="h-12 w-12 shrink-0 border border-[#0F0F0F]/20 flex items-center justify-center">
            <Heart className={`h-5 w-5 ${item.is_liked ? "fill-[#C5A059] text-[#C5A059]" : ""}`} strokeWidth={1.5} />
          </button>
          <button data-testid="mobile-buy-now-button" disabled={soldOut || missing} onClick={missing ? () => buyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }) : buyNow} className="h-12 px-6 shrink-0 bg-[#0F0F0F] text-[#C5A059] text-xs tracking-[0.25em] uppercase disabled:opacity-40">
            {soldOut ? "Sold out" : missing ? "Pick options" : "Buy now"}
          </button>
        </div>
      )}
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div data-testid="product-page" className="bg-[#FBF9F5] min-h-screen">
    <NavBar />
    <main className="px-4 sm:px-8 lg:px-12 pt-28 sm:pt-32 lg:pt-36 pb-32 sm:pb-24">
      <Link to="/" data-testid="product-back-link" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] hover:text-[#0F0F0F] mb-10">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to the edit
      </Link>
      {children}
    </main>
  </div>
);
